import { useEffect, useState } from "react";
import api, { API_BASE, openPdf, downloadCsv, normalizeDecimal, DECIMAL_HINT, containerBase } from "../lib/api";
import PageHeader from "../components/PageHeader";
import Pagination from "../components/Pagination";
import { MhdBadge, StatusBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Printer, Filter, Search, Download, CheckCircle2, Ban, Move, Trash2, History, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../context/ConfirmContext";
import ShelfSelect from "../components/ShelfSelect";
import PhotoAttachments from "../components/PhotoAttachments";
import { useRef } from "react";
import { ScanLine } from "lucide-react";
import { beep, normSku } from "../lib/scan";

const emptyBatch = { item_id: "", charge_number: "", mhd: "", quantity: 0, source: "purchase", notes: "", location: "" };

// Helper: build default form state with prefilled charge prefix (YYYY-MM-DD-) and MHD +450d
function _makeInitialForm() {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const mhd = new Date(today);
  mhd.setDate(mhd.getDate() + 450);
  return { ...emptyBatch, charge_number: `${iso(today)}-`, mhd: iso(mhd) };
}

export default function Batches() {
  const [batches, setBatches] = useState([]);
  const [items, setItems] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(_makeInitialForm());
  const [rxScan, setRxScan] = useState(false);
  const [rxVal, setRxVal] = useState("");
  const [rxFlash, setRxFlash] = useState(null);
  const rxRef = useRef(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [selected, setSelected] = useState(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTo, setMoveTo] = useState("");
  // Verwendungshistorie
  const [historyFor, setHistoryFor] = useState(null);
  const [editFor, setEditFor] = useState(null);   // {id, charge_number, mhd, notes, quality_status}
  const [createdReceive, setCreatedReceive] = useState(null); // {id, charge} nach Charge-Anlage → Foto-Upload
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const confirm = useConfirm();

  const load = async () => {
    const params = { page, page_size: pageSize };
    if (filter === "expiring") params.expiring_days = 30;
    const r = await api.get("/batches", { params });
    setBatches(r.data);
    setTotal(Number(r.headers["x-total-count"]) || r.data.length);
  };
  useEffect(() => { load(); }, [filter, page, pageSize]); // eslint-disable-line
  useEffect(() => { setPage(1); }, [filter, q]);
  useEffect(() => { api.get("/items").then((r) => setItems(r.data)); }, []);
  useEffect(() => { api.get("/product-groups").then((r) => setProductGroups(r.data)).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/batches", { ...form, quantity: Number(form.quantity) });
      toast.success("Charge angelegt");
      setCreatedReceive({ id: data.id || data._id, charge: data.charge_number || form.charge_number });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const handleReceiveScan = (raw) => {
    const code = normSku(raw);
    if (!code) return;
    const it = items.find((x) => normSku(x.barcode) === code || normSku(x.sku) === code);
    if (!it) {
      beep("warn"); setRxFlash("red"); toast.error("Artikel nicht gefunden");
    } else {
      const pg = productGroups.find((g) => g.name === it.product_group);
      const pgcode = pg?.code && String(pg.code).trim();
      setForm((f) => {
        let charge = f.charge_number;
        if (pgcode && /^\d{4}-\d{2}-\d{2}-$/.test(charge || "")) charge = `${charge}${pgcode}`;
        return { ...f, item_id: it.id, charge_number: charge };
      });
      beep("ok"); setRxFlash("green"); toast.success(`${it.sku} · ${it.name}`);
    }
    setTimeout(() => setRxFlash(null), 700);
  };

  const printLabel = (id) => openPdf(`/batches/${id}/label.pdf`).catch((err) =>
    toast.error(err.response?.status === 401 ? "Bitte neu anmelden" : "Fehler beim Öffnen des Etiketts"));

  const release = async (id, charge) => {
    try {
      await api.post(`/batches/${id}/release`);
      toast.success(`Charge ${charge} freigegeben`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const block = async (id, charge) => {
    const reason = window.prompt(`Grund für Sperrung von ${charge}?`, "");
    if (reason === null) return;
    try {
      await api.post(`/batches/${id}/block`, { reason });
      toast.success(`Charge ${charge} gesperrt`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const editLocation = async (b) => {
    const loc = window.prompt(`Regal / Lagerplatz für Charge ${b.charge_number}?`, b.location || "");
    if (loc === null) return;
    try {
      await api.patch(`/batches/${b.id}`, { location: loc });
      toast.success("Regal aktualisiert");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const saveEdit = async () => {
    if (!editFor) return;
    try {
      const payload = {
        charge_number: editFor.charge_number,
        mhd: editFor.mhd,
        notes: editFor.notes || null,
        quality_status: editFor.quality_status,
      };
      await api.patch(`/batches/${editFor.id}`, payload);
      toast.success("Charge aktualisiert");
      setEditFor(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const filtered = batches.filter((b) => {
    if (filter === "pending" && b.quality_status !== "pending") return false;
    if (filter === "passed" && b.quality_status !== "passed") return false;
    if (filter === "failed" && b.quality_status !== "failed") return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (b.charge_number || "").toLowerCase().includes(s)
        || (b.item_name || "").toLowerCase().includes(s)
        || (b.item_sku || "").toLowerCase().includes(s);
  });

  // -------- Bulk actions --------
  const filteredIds = filtered.map((b) => b.id);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const someSelected = filteredIds.some((id) => selected.has(id));
  const toggleOne = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filteredIds));
  };
  const clearSelection = () => setSelected(new Set());

  const bulkRelease = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    const ok = await confirm({
      title: `${ids.length} Chargen freigeben?`,
      description: "Qualitätsstatus wird auf 'Freigegeben' gesetzt.",
      confirmLabel: "Freigeben",
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/batches/bulk-release", { ids });
      toast.success(`${data.modified} Chargen freigegeben`);
      clearSelection(); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const bulkBlock = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    const ok = await confirm({
      title: `${ids.length} Chargen sperren?`,
      description: "Gesperrte Chargen sind vom Versand ausgeschlossen (FEFO überspringt sie).",
      confirmLabel: "Sperren",
      variant: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/batches/bulk-block", { ids });
      toast.success(`${data.modified} Chargen gesperrt`);
      clearSelection(); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const bulkMove = async () => {
    if (!moveTo.trim()) return toast.error("Zielregal fehlt");
    const ids = [...selected];
    try {
      const { data } = await api.post("/batches/bulk-move", { ids, new_location: moveTo.trim() });
      toast.success(`${data.moved} Chargen umgelagert nach ${data.target}`);
      (data.skipped || []).forEach((s) => toast.warning(`${s.charge_number || s.id.slice(-6)}: ${s.reason}`));
      setMoveOpen(false); setMoveTo(""); clearSelection(); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    const first = await confirm({
      title: `${ids.length} Chargen löschen?`,
      description: "Chargen mit Restbestand werden separat bestätigt. Diese Aktion kann nicht rückgängig gemacht werden.",
      confirmLabel: `${ids.length} löschen`,
      variant: "danger",
    });
    if (!first) return;
    try {
      const { data } = await api.post("/batches/bulk-delete", { ids });
      if (data.deleted > 0) toast.success(`${data.deleted} Chargen gelöscht`);
      const stockBlocked = (data.skipped || []).filter((s) => s.code === "stock");
      if (stockBlocked.length > 0) {
        const details = stockBlocked.map((s) => ({
          left: s.charge_number || s.id.slice(-8),
          right: `Bestand ${s.stock}`,
        }));
        const force = await confirm({
          title: `${stockBlocked.length} Chargen haben noch Bestand`,
          description: "Bestand-Bewegungen werden mit gelöscht.",
          confirmLabel: "Trotzdem löschen",
          variant: "danger",
          details,
        });
        if (force) {
          const forceIds = stockBlocked.map((s) => s.id);
          const { data: f } = await api.post("/batches/bulk-delete", { ids: forceIds, force: true });
          if (f.deleted > 0) toast.success(`${f.deleted} weitere Chargen gelöscht`);
        }
      }
      clearSelection(); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  return (
    <div data-testid="page-batches">
      <PageHeader
        eyebrow="Rückverfolgbarkeit"
        title="Chargen & MHD"
        description="Jede Charge, jede Menge, jede Bewegung. Klick auf Drucken für Etikett mit Barcode + QR."
        actions={
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setForm(_makeInitialForm()); setCreatedReceive(null); } else { setCreatedReceive(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="btn-new-batch" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
                <Plus size={16} className="mr-1" /> Neue Charge
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">{createdReceive ? "Fotos zum Wareneingang" : "Neue Charge"}</DialogTitle></DialogHeader>
              {createdReceive ? (
                <div className="space-y-4" data-testid="batch-receive-photos">
                  <div className="text-sm text-[var(--text-muted)]">Charge <b className="font-mono text-[var(--text)]">{createdReceive.charge}</b> angelegt. Optional bis zu 3 Fotos / Lieferschein-Scan hinzufügen.</div>
                  <PhotoAttachments refType="receive" refId={createdReceive.id} maxPhotos={3} label="Fotos / Lieferschein (max. 3)" />
                  <DialogFooter>
                    <Button type="button" onClick={() => { setOpen(false); setCreatedReceive(null); setForm(_makeInitialForm()); }}
                            data-testid="btn-finish-receive-photos"
                            className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">Fertig</Button>
                  </DialogFooter>
                </div>
              ) : (
              <form onSubmit={save} className="space-y-4">
                <div>
                  <Label>Artikel *</Label>
                  <div className="mb-2">
                    <button type="button" onClick={() => setRxScan((v) => !v)} data-testid="batch-scan-toggle"
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-xs font-bold border transition-colors ${rxScan ? "bg-green-600 text-white border-green-600" : "border-[var(--border)] text-[var(--text-muted)] hover:border-green-600 hover:text-green-600"}`}>
                      <ScanLine size={14} /> {rxScan ? "Scanner aktiv" : "Scanner"}
                    </button>
                    {rxScan && (
                      <Input ref={rxRef} autoFocus value={rxVal} data-testid="batch-scan-input"
                             onChange={(e) => setRxVal(e.target.value)}
                             onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleReceiveScan(rxVal); setRxVal(""); } }}
                             onBlur={() => { if (rxScan) setTimeout(() => rxRef.current?.focus(), 60); }}
                             placeholder="Barcode scannen (SKU)…"
                             className={`mt-2 h-10 ${rxFlash === "green" ? "border-green-500 ring-2 ring-green-400" : rxFlash === "red" ? "border-red-500 ring-2 ring-red-400" : ""}`} />
                    )}
                  </div>
                  <Select value={form.item_id} onValueChange={(v) => {
                    // On item selection, auto-complete charge_number if a product-group code exists
                    const it = items.find((x) => x.id === v);
                    const pg = it && productGroups.find((g) => g.name === it.product_group);
                    const code = pg?.code && String(pg.code).trim();
                    let charge = form.charge_number;
                    // Only auto-fill when the current value is still the naked date-prefix
                    if (code && /^\d{4}-\d{2}-\d{2}-$/.test(charge || "")) {
                      charge = `${charge}${code}`;
                    }
                    setForm({ ...form, item_id: v, charge_number: charge });
                  }} required>
                    <SelectTrigger data-testid="select-batch-item"><SelectValue placeholder="Artikel wählen" /></SelectTrigger>
                    <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.sku} · {i.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Chargen-Nr.
                      <span className="text-[10px] text-[var(--text-muted)] ml-1">(leer = Auto)</span>
                    </Label>
                    <Input data-testid="input-batch-charge"
                           placeholder="YYYY-MM-DD-NNN (3-stellige Warengruppe)"
                           value={form.charge_number}
                           onChange={(e) => setForm({ ...form, charge_number: e.target.value })} />
                    <div className="text-[10px] text-[var(--text-muted)] mt-1">
                      Datums-Präfix ist vorbelegt — trage nach dem letzten „-" die 3-stellige Warengruppe ein (z.B. 601).
                    </div>
                  </div>
                  <div><Label>MHD (JJJJ-MM-TT) *</Label><Input required type="date" data-testid="input-batch-mhd" value={form.mhd} onChange={(e) => setForm({ ...form, mhd: e.target.value })} /></div>
                  <div><Label>Menge *</Label><Input required type="number" step="any" placeholder={DECIMAL_HINT} data-testid="input-batch-qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: normalizeDecimal(e.target.value) })} /></div>
                  <div><Label>Regal / Lagerplatz</Label>
                    <ShelfSelect
                      value={form.location}
                      onChange={(v) => setForm({ ...form, location: v })}
                      itemType={items.find((it) => it.id === form.item_id)?.type}
                      itemId={form.item_id}
                      quantity={Number(form.quantity) || 0}
                      placeholder="z.B. A-01-03"
                      testId="input-batch-location"
                    />
                  </div>
                  <div>
                    <Label>Quelle</Label>
                    <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="purchase">Wareneingang</SelectItem>
                        <SelectItem value="production">Produktion</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Notizen</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <DialogFooter>
                  <Button type="submit" data-testid="btn-save-batch" className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">Speichern</Button>
                </DialogFooter>
              </form>
              )}
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input placeholder="Suche Charge / Artikel / SKU…" value={q} onChange={(e) => setQ(e.target.value)}
                 className="pl-8 rounded-xl h-9" data-testid="input-batches-search" />
        </div>
        <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]"><Filter size={14} /> Filter:</div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-56 rounded-xl" data-testid="select-batch-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Chargen</SelectItem>
            <SelectItem value="expiring">MHD ≤ 30 Tage</SelectItem>
            <SelectItem value="pending">QC: in Prüfung</SelectItem>
            <SelectItem value="passed">QC: freigegeben</SelectItem>
            <SelectItem value="failed">QC: gesperrt</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="btn-export-batches" variant="outline" className="rounded-full"
                onClick={() => downloadCsv("/exports/batches.csv", "chargen.csv")}>
          <Download size={14} className="mr-1" /> CSV
        </Button>
      </div>

      {selected.size > 0 && (
        <div className="sticky top-2 z-30 mb-4 rounded-2xl bg-black text-white p-4 flex items-center justify-between gap-4 flex-wrap shadow-2xl shadow-black/40"
             data-testid="batches-bulk-toolbar">
          <div className="text-sm">
            <span className="font-bold">{selected.size}</span> ausgewählt
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={clearSelection} variant="outline" size="sm"
                    className="rounded-full bg-transparent text-white border-white/40 hover:bg-white/10"
                    data-testid="btn-clear-batch-selection">Abwählen</Button>
            <Button onClick={bulkRelease} size="sm" data-testid="btn-bulk-release"
                    className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold">
              <CheckCircle2 size={14} className="mr-1" /> Freigeben
            </Button>
            <Button onClick={() => setMoveOpen(true)} size="sm" data-testid="btn-bulk-move"
                    className="rounded-full bg-[var(--pop-yellow)] hover:bg-[var(--pop-yellow-dark)] text-black font-bold">
              <Move size={14} className="mr-1" /> Umlagern
            </Button>
            <Button onClick={bulkBlock} size="sm" data-testid="btn-bulk-block"
                    className="rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold">
              <Ban size={14} className="mr-1" /> Sperren
            </Button>
            <Button onClick={bulkDelete} size="sm" data-testid="btn-bulk-delete-batches"
                    className="rounded-full bg-red-600 hover:bg-red-700 text-white font-bold">
              <Trash2 size={14} className="mr-1" /> Löschen
            </Button>
          </div>
        </div>
      )}

      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent data-testid="dialog-bulk-move-batches" aria-describedby="bulk-move-desc">
          <DialogHeader><DialogTitle className="font-display text-2xl">{selected.size} Chargen umlagern</DialogTitle></DialogHeader>
          <div id="bulk-move-desc" className="text-sm text-[var(--text-muted)] mb-3">Zielregal für alle markierten Chargen. Chargen, die bereits dort liegen, werden übersprungen.</div>
          <Label>Zielregal *</Label>
          <Input placeholder="z.B. A-01-03" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}
                 data-testid="input-bulk-move-target" className="font-mono" />
          <DialogFooter className="mt-3 gap-2">
            <Button type="button" variant="outline" className="rounded-full" onClick={() => setMoveOpen(false)}>Abbrechen</Button>
            <Button onClick={bulkMove} data-testid="btn-confirm-bulk-move"
                    className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
              Umlagern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden w-full">
        {/* Desktop: Tabelle */}
        <div className="hidden md:block overflow-x-auto">
        <Table className="table-zebra">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={toggleAll}
                  data-testid="checkbox-select-all-batches"
                  aria-label="Alle Chargen auswählen"
                />
              </TableHead>
              <TableHead className="overline">Charge</TableHead>
              <TableHead className="overline">Artikel</TableHead>
              <TableHead className="overline">MHD</TableHead>
              <TableHead className="overline">Rest</TableHead>
              <TableHead className="overline">Bestand</TableHead>
              <TableHead className="overline">Ausschuss</TableHead>
              <TableHead className="overline">Regal</TableHead>
              <TableHead className="overline">Qualität</TableHead>
              <TableHead className="overline text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((b) => (
              <TableRow key={b.id} data-testid={`batch-row-${b.charge_number}`}>
                <TableCell>
                  <Checkbox
                    checked={selected.has(b.id)}
                    onCheckedChange={() => toggleOne(b.id)}
                    data-testid={`checkbox-batch-${b.charge_number}`}
                    aria-label={`${b.charge_number} auswählen`}
                  />
                </TableCell>
                <TableCell className="font-mono">
                  {b.charge_number}
                  {b.container_count && b.container_size && (
                    <div className="text-[10px] text-orange-700" data-testid={`batch-gebinde-${b.charge_number}`}>
                      {b.container_count} {b.container_label || "Gebinde"} à {b.container_size} {b.container_unit || "kg"} = {Number(b.quantity || 0).toLocaleString("de-DE")} {containerBase(b.container_unit)}
                    </div>
                  )}
                </TableCell>
                <TableCell><div className="font-medium">{b.item_name}</div><div className="text-xs font-mono text-[var(--text-muted)]">{b.item_sku}</div></TableCell>
                <TableCell className="font-mono text-xs">{b.mhd}</TableCell>
                <TableCell><MhdBadge days={b.days_to_mhd} /></TableCell>
                <TableCell className="font-mono text-xs">{b.current_stock} {b.item_unit}</TableCell>
                <TableCell className="font-mono text-xs" data-testid={`waste-${b.charge_number}`}>
                  {b.waste_bags > 0
                    ? <span className="pop-pill bg-red-50 text-red-700 border border-red-200 font-bold text-[10px]">{b.waste_bags} Tüten</span>
                    : <span className="text-[var(--text-muted)]">—</span>}
                </TableCell>
                <TableCell>
                  <button
                    onClick={() => editLocation(b)}
                    data-testid={`btn-location-${b.charge_number}`}
                    className="font-mono text-xs hover:text-[var(--pop-pink)] hover:underline"
                    title="Regal bearbeiten"
                  >
                    {b.location || "—"}
                  </button>
                </TableCell>
                <TableCell><StatusBadge status={b.quality_status} /></TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-3 justify-end">
                    {b.quality_status !== "passed" && (
                      <button
                        onClick={() => release(b.id, b.charge_number)}
                        data-testid={`btn-release-${b.charge_number}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--success)] hover:underline"
                      >
                        <CheckCircle2 size={14} /> Freigeben
                      </button>
                    )}
                    {b.quality_status !== "failed" && (
                      <button
                        onClick={() => block(b.id, b.charge_number)}
                        data-testid={`btn-block-${b.charge_number}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--danger)] hover:underline"
                      >
                        <Ban size={14} /> Sperren
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        setHistoryFor(b);
                        setHistoryLoading(true);
                        setHistoryData(null);
                        try {
                          const { data } = await api.get(`/batches/${b.id}/usage-history`);
                          setHistoryData(data);
                        } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
                        finally { setHistoryLoading(false); }
                      }}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--pop-pink)] hover:underline"
                      data-testid={`btn-history-${b.charge_number}`}
                      title="Verwendungshistorie / Rückverfolgbarkeit"
                    >
                      <History size={14} /> Historie
                    </button>
                    <button
                      onClick={() => setEditFor({
                        id: b.id, charge_number: b.charge_number || "",
                        mhd: b.mhd || "", notes: b.notes || "",
                        quality_status: b.quality_status || "pending",
                      })}
                      data-testid={`btn-edit-batch-${b.charge_number}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--pop-pink)] hover:underline"
                      title="Charge bearbeiten"
                    >
                      <Pencil size={14} /> Bearbeiten
                    </button>
                    <button
                      onClick={() => printLabel(b.id)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--text)] hover:text-[var(--pop-caramel)]"
                      data-testid={`btn-label-${b.charge_number}`}
                    >
                      <Printer size={14} /> Etikett
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {batches.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-[var(--text-muted)]">Keine Chargen angelegt.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        </div>

        {/* Mobile: Karten-Ansicht */}
        <div className="md:hidden divide-y divide-[var(--border)]">
          {filtered.map((b) => (
            <div key={b.id} className="p-3 space-y-2" data-testid={`batch-card-${b.charge_number}`}>
              <div className="flex items-start gap-2">
                <Checkbox checked={selected.has(b.id)} onCheckedChange={() => toggleOne(b.id)}
                          aria-label={`${b.charge_number} auswählen`} />
                <div className="flex-1 min-w-0">
                  <div className="font-mono font-bold text-sm">{b.charge_number}</div>
                  <div className="text-sm">{b.item_name}</div>
                  <div className="text-xs font-mono text-[var(--text-muted)]">{b.item_sku}</div>
                </div>
                <StatusBadge status={b.quality_status} />
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-[var(--text-muted)]">MHD:</span> <span className="font-mono">{b.mhd}</span> <MhdBadge days={b.days_to_mhd} /></div>
                <div><span className="text-[var(--text-muted)]">Bestand:</span> <span className="font-mono font-bold">{b.current_stock} {b.item_unit}</span></div>
                <div className="col-span-2">
                  <span className="text-[var(--text-muted)]">Regal:</span>{" "}
                  <button onClick={() => editLocation(b)} className="font-mono hover:text-[var(--pop-pink)] hover:underline">
                    {b.location || "—"}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap pt-1">
                {b.quality_status !== "passed" && (
                  <button onClick={() => release(b.id, b.charge_number)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--success)]">
                    <CheckCircle2 size={14} /> Freigeben
                  </button>
                )}
                {b.quality_status !== "failed" && (
                  <button onClick={() => block(b.id, b.charge_number)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--danger)]">
                    <Ban size={14} /> Sperren
                  </button>
                )}
                <button onClick={() => setEditFor({
                  id: b.id, charge_number: b.charge_number || "",
                  mhd: b.mhd || "", notes: b.notes || "",
                  quality_status: b.quality_status || "pending",
                })} className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--pop-pink)]">
                  <Pencil size={14} /> Bearbeiten
                </button>
                <button onClick={() => printLabel(b.id)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--pop-caramel)] ml-auto">
                  <Printer size={14} /> Etikett
                </button>
              </div>
            </div>
          ))}
          {batches.length === 0 && (
            <div className="text-center py-10 text-[var(--text-muted)]">Keine Chargen angelegt.</div>
          )}
        </div>
        <Pagination page={page} pageSize={pageSize} total={total} onChange={setPage} onPageSizeChange={(n)=>{setPage(1);setPageSize(n);}} storageKey="batches" testIdPrefix="batches-pager" />
      </div>

      {/* Bearbeiten Dialog */}
      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-edit-batch">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Charge bearbeiten</DialogTitle>
          </DialogHeader>
          {editFor && (
            <div className="space-y-3">
              <div>
                <Label>Chargen-Nr.</Label>
                <Input value={editFor.charge_number} data-testid="input-edit-batch-charge"
                       onChange={(e) => setEditFor({ ...editFor, charge_number: e.target.value })} />
              </div>
              <div>
                <Label>MHD</Label>
                <Input type="date" value={(editFor.mhd || "").slice(0, 10)}
                       data-testid="input-edit-batch-mhd"
                       onChange={(e) => setEditFor({ ...editFor, mhd: e.target.value })} />
              </div>
              <div>
                <Label>Qualitätsstatus</Label>
                <Select value={editFor.quality_status}
                        onValueChange={(v) => setEditFor({ ...editFor, quality_status: v })}>
                  <SelectTrigger data-testid="select-edit-batch-quality"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Ausstehend</SelectItem>
                    <SelectItem value="passed">Freigegeben</SelectItem>
                    <SelectItem value="failed">Gesperrt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notizen</Label>
                <Input value={editFor.notes || ""}
                       onChange={(e) => setEditFor({ ...editFor, notes: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditFor(null)} className="rounded-full">Abbrechen</Button>
            <Button onClick={saveEdit} data-testid="btn-save-edit-batch"
                    className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verwendungshistorie Dialog */}
      <Dialog open={!!historyFor} onOpenChange={(o) => !o && (setHistoryFor(null), setHistoryData(null))}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <History size={22} className="text-[var(--pop-pink)]" />
              Verwendungshistorie – {historyFor?.charge_number}
            </DialogTitle>
          </DialogHeader>
          {historyLoading && <div className="py-10 text-center text-[var(--text-muted)]">Lade Rückverfolgbarkeit…</div>}
          {historyData && (
            <div className="space-y-5">
              <div className="bg-[var(--pop-cream)]/40 border border-[var(--border)] rounded-xl p-3 text-sm grid md:grid-cols-4 gap-x-6 gap-y-1">
                <div><span className="text-[var(--text-muted)]">Artikel:</span> <b>{historyData.item.name}</b></div>
                <div><span className="text-[var(--text-muted)]">SKU:</span> <span className="font-mono">{historyData.item.sku}</span></div>
                <div><span className="text-[var(--text-muted)]">MHD:</span> <span className="font-mono">{historyData.batch.mhd}</span></div>
                <div><span className="text-[var(--text-muted)]">Restbestand:</span> <b>{historyData.current_stock} {historyData.item.unit}</b></div>
                <div><span className="text-[var(--text-muted)]">Regal:</span> <span className="font-mono">{historyData.batch.location || "—"}</span></div>
                <div><span className="text-[var(--text-muted)]">QC:</span> <span className="font-mono">{historyData.batch.quality_status}</span></div>
                <div><span className="text-[var(--text-muted)]">Quelle:</span> <span className="font-mono">{historyData.batch.source}</span></div>
                <div><span className="text-[var(--text-muted)]">Erstellt:</span> <span className="font-mono text-xs">{(historyData.batch.created_at || "").slice(0,10)}</span></div>
              </div>

              <PhotoAttachments refType="receive" refId={historyData.batch.id || historyFor?.id} maxPhotos={3} label="Fotos (Wareneingang)" />

              {historyData.produced_by && (
                <div className="border-2 border-blue-200 bg-blue-50 rounded-xl p-3" data-testid="history-produced-by">
                  <div className="overline text-blue-800 mb-1">Erzeugt durch Produktionsauftrag</div>
                  <div className="text-sm">
                    <b className="font-mono">{historyData.produced_by.number}</b>
                    {historyData.produced_by.recipe_name && <> · Rezeptur: {historyData.produced_by.recipe_name}</>}
                    {historyData.produced_by.quantity && <> · Menge: {historyData.produced_by.quantity}</>}
                    {historyData.produced_by.completed_at && <> · abgeschlossen {historyData.produced_by.completed_at.slice(0,10)}</>}
                  </div>
                </div>
              )}

              {/* Warenausgänge / Lieferscheine */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display font-bold text-lg">Warenausgang (Lieferscheine)</h3>
                  <span className="pop-pill bg-pink-100 text-pink-800 text-xs" data-testid="history-delivery-total">
                    Σ {historyData.totals.delivered} {historyData.item.unit} · {historyData.totals.delivery_count} LS
                  </span>
                </div>
                <div className="border border-[var(--border)] rounded-xl overflow-hidden">
                  <Table className="table-zebra">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="overline">Lieferschein</TableHead>
                        <TableHead className="overline">Kunde</TableHead>
                        <TableHead className="overline">Datum</TableHead>
                        <TableHead className="overline">Status</TableHead>
                        <TableHead className="overline text-right">Menge</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyData.deliveries.map((d) => (
                        <TableRow key={d.id} data-testid={`history-ls-${d.number}`}>
                          <TableCell className="font-mono text-xs">
                            {d.number}
                            {d.invoice_number && <div className="text-[10px] text-[var(--text-muted)]">RE {d.invoice_number}</div>}
                            {d.is_sample && <span className="ml-1 pop-pill bg-pink-100 text-pink-800 text-[9px]">PROBE</span>}
                          </TableCell>
                          <TableCell className="text-sm">{d.customer_name}</TableCell>
                          <TableCell className="font-mono text-xs">{(d.delivery_date || "").slice(0,10)}</TableCell>
                          <TableCell><StatusBadge status={d.status || "draft"} /></TableCell>
                          <TableCell className="text-right font-mono text-sm font-semibold">{d.quantity} {historyData.item.unit}</TableCell>
                        </TableRow>
                      ))}
                      {historyData.deliveries.length === 0 && (
                        <TableRow><TableCell colSpan={5} className="text-center py-6 text-[var(--text-muted)] text-sm">Diese Charge wurde noch nicht ausgeliefert.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Produktions-Verbrauch */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display font-bold text-lg">Verbraucht in Produktion</h3>
                  <span className="pop-pill bg-yellow-100 text-yellow-800 text-xs" data-testid="history-production-total">
                    Σ {historyData.totals.consumed_in_production} {historyData.item.unit} · {historyData.totals.production_count} PA
                  </span>
                </div>
                <div className="border border-[var(--border)] rounded-xl overflow-hidden">
                  <Table className="table-zebra">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="overline">Auftrag</TableHead>
                        <TableHead className="overline">Rezeptur</TableHead>
                        <TableHead className="overline">Status</TableHead>
                        <TableHead className="overline">Datum</TableHead>
                        <TableHead className="overline text-right">Verbraucht</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyData.production_consumed.map((p, idx) => (
                        <TableRow key={idx} data-testid={`history-pa-${p.number}`}>
                          <TableCell className="font-mono text-xs">{p.number || "—"}</TableCell>
                          <TableCell className="text-sm">{p.recipe_name || "—"}</TableCell>
                          <TableCell>{p.status ? <StatusBadge status={p.status} /> : "—"}</TableCell>
                          <TableCell className="font-mono text-xs">{(p.completed_at || p.picked_at || "").slice(0,10) || "—"}</TableCell>
                          <TableCell className="text-right font-mono text-sm font-semibold">{p.quantity_used} {historyData.item.unit}</TableCell>
                        </TableRow>
                      ))}
                      {historyData.production_consumed.length === 0 && (
                        <TableRow><TableCell colSpan={5} className="text-center py-6 text-[var(--text-muted)] text-sm">Diese Charge wurde noch nicht in der Produktion verbraucht.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Bewegungshistorie */}
              <details className="border border-[var(--border)] rounded-xl">
                <summary className="p-3 cursor-pointer text-sm font-semibold" data-testid="history-movements-toggle">
                  Alle Bewegungen ({historyData.movements.length})
                </summary>
                <div className="border-t border-[var(--border)] max-h-64 overflow-y-auto">
                  <Table className="table-zebra">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="overline">Zeit</TableHead>
                        <TableHead className="overline">Typ</TableHead>
                        <TableHead className="overline text-right">Menge</TableHead>
                        <TableHead className="overline">Referenz</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyData.movements.map((m) => {
                        const sign = m.type === "out" || m.type === "production_out" ? "−" : "+";
                        const color = sign === "+" ? "text-[var(--success)]" : "text-red-700";
                        return (
                          <TableRow key={m.id}>
                            <TableCell className="font-mono text-[11px]">{(m.created_at || "").slice(0,16).replace("T", " ")}</TableCell>
                            <TableCell className="text-xs">{m.type}</TableCell>
                            <TableCell className={`text-right font-mono text-xs ${color}`}>{sign}{Math.abs(m.quantity)}</TableCell>
                            <TableCell className="text-xs font-mono">{m.reference || "—"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </details>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
